import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {Button, Type} from '@Components/button';
import styles from './publishing-share-to-app-panel.module.scss';
import {LOADER_SIZE} from '@Components/circular-progress-loader';
import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import {getUserPremiumLevel} from '@Libraries/user.library';
import {Text, TextSize} from '@Components/text';
import {ToggleSwitch} from '@Components/toggle-switch-v2';
import {useFreebieProduct, useHashedId, usePosterPreviewUrl, usePrintProduct} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import {setErrorMessageForPublishingPanel} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {PublishingLoaderScreen} from '@Panels/poster-publishing-options-panel/components/publishing-loader-screen/publishing-loader-screen';
import {Status} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {getAssetUrl} from '@Utils/s3.util';
import {useAppDispatch} from '@/hooks';
import type {AJAXisHighResResponse, ShareToAppCheckoutResponse} from './publishing-share-to-app-panel.types';
import {doWebShare, isNewPremiumDownload, onShareToApp, onWebShareError, ORDER_REGEN_RESPONSE, SHARE_TO_APP_UNDEFINED} from './publishing-share-to-app-library';

export function PublishingShareToAppPanel(): ReactElement<any> {
  const [isLoading, setIsLoading] = useState(true);
  const [isHighRes, setIsHighRes] = useState(false);
  const [idProduct, setIdProduct] = useState(0);
  const freebieProduct = useFreebieProduct();
  const printProduct = usePrintProduct();
  const [removeWatermark, setRemoveWatermark] = useState(false);
  const productUsed = printProduct?.needsPurchase ? freebieProduct : printProduct;
  const posterHashedId = useHashedId();
  const image: string = usePosterPreviewUrl();
  const [fileToShare, setFileToShare] = useState<File>(new File([], ''));
  const [canShare, setCanShare] = useState(false);
  const dispatch = useAppDispatch();
  const orderId = printProduct?.orderId;
  const userPremiumLevel = getUserPremiumLevel();
  const readyImage = getAssetUrl('publishing-design-ready.png');

  const getShareToAppPosterData = async (): Promise<void> => {
    try {
      setIsLoading(true);
      if (window.PMW.isPosterMaker()) {
        await window.PMW.writeLocal('poster/getPosterDataForSharingToApp/', {
          hid: posterHashedId,
        });
      }
      canUserShareHighRes();
      setIsLoading(false);
    } catch (e) {
      onWebShareError();
    }
  };

  useEffect(() => {
    void getShareToAppPosterData();
  }, []);

  const isShareToAppAvailable = (): boolean => {
    return (window.PMW.isMobileOS() && typeof navigator !== 'undefined' && navigator.canShare?.bind(navigator)) as boolean;
  };

  const canUserShareHighRes = (): void => {
    if (isShareToAppAvailable()) {
      void window.PMW.readLocal('socialmedia/canUserShareHighRes', {hashedId: posterHashedId}).then((response: AJAXisHighResResponse) => {
        if (response) {
          setIdProduct(Number(response.idproduct));
          setIsHighRes(response.highres);
        }
      });
    }
  };

  const onShareToAppFreeCorrection = (): void => {
    void window.PMW.writeLocal('cart/useFreeCorrectionNew', {
      idorder: orderId,
    })
      .then(doWebShareHelper)
      .catch(() => {
        onWebShareError();
      });
  };

  const onShareToAppCheckoutSuccess = (response: ShareToAppCheckoutResponse): void => {
    if (response.orderRegen) {
      doWebShareHelper();
      return;
    }
    parseResponseForCheckout(response);
  };

  const onShareToAppCheckout = (): void => {
    void window.PMW.writeLocal('cart/checkoutpublish', {
      idposter: posterHashedId,
      idproduct: idProduct,
      promotion: '',
      mode: 'share-to-app',
    })
      .then((response: ShareToAppCheckoutResponse) => {
        onShareToAppCheckoutSuccess(response);
      })
      .catch(() => {
        onWebShareError();
      });
  };

  const processDesignForShareToApp = (): void => {
    setIsLoading(true);
    if (!removeWatermark && userPremiumLevel === USER_PREMIUM_LEVELS.PAYG) {
      doWebShareHelper();
    } else if (Number(orderId) !== 0 && userPremiumLevel === USER_PREMIUM_LEVELS.PAYG) {
      onShareToAppFreeCorrection();
    } else {
      onShareToAppCheckout();
    }
  };

  const newPremiumDowloadForShareToApp = (): void => {
    void window.PMW.writeLocal('cart/newPremiumDownload/', {
      id: idProduct,
      hid: posterHashedId,
      is: 1,
    })

      .then(doWebShareHelper)
      .catch(() => {
        dispatch(setErrorMessageForPublishingPanel(window.i18next.t('pmwjs_error_message')));
      });
  };

  const rawPriceForShareToApp = (response: ShareToAppCheckoutResponse): void => {
    window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
      window.PMW.postCheckoutProcessing(response, null, doWebShareHelper);
    });
  };

  const isOrderRegen = (response: ShareToAppCheckoutResponse): boolean => {
    return !!response.orderRegen && response.orderRegen === ORDER_REGEN_RESPONSE;
  };

  const doWebShareHelper = (): void => {
    doWebShare(productUsed, posterHashedId, isHighRes, setCanShare, setIsLoading, setFileToShare);
  };

  const parseResponseForCheckout = (response: ShareToAppCheckoutResponse): void => {
    if (isOrderRegen(response)) {
      doWebShareHelper();
      return;
    }
    if (isNewPremiumDownload(response)) {
      newPremiumDowloadForShareToApp();
    } else if (response.status && response.status === Status.DOWNLOADABLE) {
      doWebShareHelper();
    } else if (response.message && response.message.length !== 0) {
      onWebShareError();
    } else if (typeof response.checkout['raw-price'] !== SHARE_TO_APP_UNDEFINED && response.checkout['raw-price'] > 0) {
      rawPriceForShareToApp(response);
    } else {
      doWebShareHelper();
    }
  };

  const onClickShareToApp = (): void => {
    if (canShare) {
      onShareToApp(productUsed, fileToShare, setIsLoading);
    } else {
      processDesignForShareToApp();
    }
  };

  return isLoading ? (
    <PublishingLoaderScreen customHeight={styles.setCustomHeight} loaderSize={LOADER_SIZE.LARGE} />
  ) : (
    <div>
      <div className={`flex-center spacing-p-t-6 spacing-p-b-4 flex-v-row ${styles.imageShadow}`}>
        <img className={`${styles.imageHeight} ${canShare ? '' : 'shadow-static-elevation-3 hover-transition-all'}`} src={canShare ? readyImage : image} alt="" />
        {canShare ? <Text size={TextSize.LARGE} className="content-body spacing-p-t-4 spacing-p-b-5" val={window.i18next.t('pmwjs_ready_to_share')} /> : ''}
      </div>
      <div className="flex-center">
        {!isHighRes && !canShare ? (
          <div className="spacing-m-t-6 spacing-m-b-6">
            <ToggleSwitch
              className="spacing-m-r-2"
              id="example"
              isChecked={removeWatermark}
              onChange={() => {
                setRemoveWatermark((removeWatermark) => !removeWatermark);
              }}
            />

            <Text val={window.i18next.t('pmwjs_remove_watermark')} size={TextSize.XSMALL} bold />
          </div>
        ) : null}
      </div>
      <div className="flex-center spacing-p-2">
        <Button isLoading={isLoading} onClick={onClickShareToApp} type={Type.PRIMARY} isFullWidth text={window.i18next.t('pmwjs_share_to_app')} />
      </div>
    </div>
  );
}
